"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  activatePartnerProgramAction,
  createPartnerCodeAction,
  requestPayoutAction,
  setPartnerCodeActiveAction,
  transferToWalletAction,
  updatePartnerCodeAction,
} from "@/server-actions/partners"
import {
  adjustPartnerBalanceAction,
  completePayoutRequestAction,
  createAdminPartnerCodeAction,
  rejectPayoutRequestAction,
  setAdminCodeCommissionAction,
  updateAdminPartnerAction,
  updateAdminProgramSettingsAction,
  type AdminProgramSettings,
} from "@/server-actions/admin-partners"
import {
  adminPartnerDetailQueryPrefix,
  adminPartnersQueryPrefix,
  adminPayoutRequestsQueryPrefix,
  adminProgramSettingsQueryPrefix,
  partnerLedgerQueryPrefix,
  partnerOverviewQueryPrefix,
  partnerPayoutsQueryPrefix,
  partnerProgramAccessQueryPrefix,
} from "@/hooks/queries/partners"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"
import { type WithQueryHandlingOptions } from "@/lib/with-query-handling"
import type { PartnerAccountStatus } from "@/lib/partners/types"
import { useTranslations } from "next-intl"


export function useActivatePartnerProgramMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(activatePartnerProgramAction({}), {
        loading: tUi("activatingPartnerProgram"),
        success: tUi("partnerProgramActivated"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: partnerOverviewQueryPrefix })
    },
  })
}

export type PartnerCodeFormInput = {
  code?: string
  label: string | null
  client_bonus_percent: number
  client_bonus_payment_limit: number | null
}

export function useSavePartnerCodeMutation(options: Pick<WithQueryHandlingOptions<unknown>, "setErrors"> = {}) {
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: PartnerCodeFormInput & { id?: string }) => {
      const { id, ...values } = input
      const result = await withQueryHandling(
        id ? updatePartnerCodeAction({ id, ...values }) : createPartnerCodeAction(values),
        {
          setErrors: options.setErrors,
          loading: id ? "Saving referral link..." : "Creating referral link...",
          success: id ? "Referral link saved" : "Referral link created",
        },
      )
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: partnerOverviewQueryPrefix })
    },
  })
}

export function useSetPartnerCodeActiveMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { id: string; is_active: boolean }) => {
      const result = await withQueryHandling(setPartnerCodeActiveAction(input), {
        loading: input.is_active ? "Activating..." : "Deactivating...",
        success: input.is_active ? "Referral link activated" : "Referral link deactivated",
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: partnerOverviewQueryPrefix })
    },
  })
}

export function useTransferToWalletMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { amount_microusd: number; reference: string }) => {
      const result = await withQueryHandling(transferToWalletAction(input), {
        loading: tUi("transferring"),
        success: tUi("movedToYourWallet"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: partnerOverviewQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: partnerLedgerQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: ["billing"] }),
      ])
    },
  })
}

export function useRequestPayoutMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { amount_microusd: number; reference: string; expected_fee_microusd: number }) => {
      const result = await withQueryHandling(requestPayoutAction(input), {
        loading: tUi("requestingPayout"),
        success: tUi("payoutRequested"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    // Awaited on refusal too: the dialog stays pending until it re-renders with the current fee, minimum and
    // balance, so the next click confirms what the partner now sees.
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: partnerOverviewQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: partnerLedgerQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: partnerPayoutsQueryPrefix }),
      ])
    },
  })
}

export function useProcessPayoutRequestMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { id: string; note?: string; decision: "complete" | "reject" }) => {
      const action = input.decision === "complete"
        ? completePayoutRequestAction({ id: input.id })
        : rejectPayoutRequestAction({ id: input.id, note: input.note ?? "" })

      const result = await withQueryHandling(action, {
        loading: input.decision === "complete" ? "Marking as paid..." : "Rejecting...",
        success: input.decision === "complete" ? "Payout marked as paid" : "Payout rejected",
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      // A rejection refunds the hold, so the balances on the partner list and the
      // open detail move too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminPayoutRequestsQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: adminPartnersQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: adminPartnerDetailQueryPrefix }),
      ])
    },
  })
}

export function useUpdateAdminPartnerMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const invalidatePartnerViews = useInvalidatePartnerAdminViews()

  return useMutation({
    mutationFn: async (input: {
      partner_id: string
      commission_percent: number | null
      status: PartnerAccountStatus
    }) => {
      const result = await withQueryHandling(updateAdminPartnerAction(input), {
        loading: tUi("savingPartner"),
        success: tUi("partnerSaved"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidatePartnerViews,
  })
}

export function useSetAdminCodeCommissionMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const invalidatePartnerViews = useInvalidatePartnerAdminViews()

  return useMutation({
    mutationFn: async (input: { code_id: string; commission_percent: number | null }) => {
      const result = await withQueryHandling(setAdminCodeCommissionAction(input), {
        loading: tUi("savingCommission"),
        success: tUi("commissionSaved"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidatePartnerViews,
  })
}

export function useCreateAdminPartnerCodeMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const invalidatePartnerViews = useInvalidatePartnerAdminViews()

  return useMutation({
    mutationFn: async (input: {
      partner_id: string
      label: string | null
      commission_percent: number | null
      client_bonus_percent: number
      client_bonus_payment_limit: number | null
    }) => {
      const result = await withQueryHandling(createAdminPartnerCodeAction(input), {
        loading: tUi("creatingReferralLink"),
        success: tUi("referralLinkCreated"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidatePartnerViews,
  })
}

export function useAdjustPartnerBalanceMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const invalidatePartnerViews = useInvalidatePartnerAdminViews()

  return useMutation({
    mutationFn: async (input: {
      partner_id: string
      amount_microusd: number
      note: string
      reference: string
    }) => {
      const result = await withQueryHandling(adjustPartnerBalanceAction(input), {
        loading: tUi("adjustingBalance"),
        success: tUi("balanceAdjusted"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidatePartnerViews,
  })
}

export function useUpdateAdminProgramSettingsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPartners")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: AdminProgramSettings) => {
      const result = await withQueryHandling(updateAdminProgramSettingsAction(input), {
        loading: tUi("savingSettings"),
        success: tUi("programSettingsSaved"),
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      // The default percent and the switch feed the partner list, every cabinet and its menu item.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminProgramSettingsQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: adminPartnersQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: adminPartnerDetailQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: partnerOverviewQueryPrefix }),
        queryClient.invalidateQueries({ queryKey: partnerProgramAccessQueryPrefix }),
      ])
    },
  })
}

/** Partner edits move numbers shown on both the list and the open detail view. */
function useInvalidatePartnerAdminViews() {
  const queryClient = useQueryClient()

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: adminPartnersQueryPrefix }),
      queryClient.invalidateQueries({ queryKey: adminPartnerDetailQueryPrefix }),
    ])
  }
}
